import { SourceType } from 'api-models'
import { GComboBox, GInput, GTooltip } from 'components/basic-blocks'
import { GCheckbox } from 'components/basic-blocks/g-checkbox'
import { ChangeEvent } from 'react'
import { CLICK_STATUS_OPTIONS, PERIOD_OPTIONS } from '../../../../constants'
import {
	setObjectSimpleValueAction,
	setPeriodUnit,
	setPeriodValue,
	setSimpleValueAction,
	toggleBlockAction,
	toggleTempExclusionAction
} from '../../reducer'
import { ActionsProps } from './types'
import { HelpCircle as AiOutlineQuestionCircle } from 'components/icons'
export const Actions = ({
	rule,
	dispatch,
	errors,
	clearError
}: ActionsProps) => {
	const isRangeFocus = rule.trigger.focus === SourceType.RANGE

	return (
		<section className="flex flex-col gap-4">
			<div className="flex  gap-2 items-center">
				<span className="text-t-base-blue font-medium text-xl">Actions</span>
				<GTooltip
					position="top"
					arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
					contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
					content="Configure what actions will be taken when the rule is triggered.">
					<AiOutlineQuestionCircle className="w-4 h-4 text-t-gray-hold" />
				</GTooltip>
			</div>

			<div className="flex items-center gap-2 h-[36px]">
				<GCheckbox
					name="click_status"
					label="Set click status"
					checked={true}
					disabled={true}
					className="text-medium items-center"
					onChange={() => null}
					labelClassName="opacity-100 text-t-google-tooltip"
				/>
				<GComboBox
					options={CLICK_STATUS_OPTIONS}
					filter={(item, query) => item.toString() === query}
					value={rule.resolve_status}
					renderOption={(i) => i.toString()}
					renderValue={(i) => i.toString()}
					onChange={(value) =>
						dispatch(setSimpleValueAction('resolve_status', value))
					}
					className="w-[40%]"
				/>
			</div>
			<div className="flex items-center gap-2 h-[36px]">
				<GCheckbox
					name="click_labels"
					label="Add labels to click"
					checked={!!rule.resolve_labels.enabled}
					className="text-bold items-center"
					onChange={() =>
						dispatch(
							setObjectSimpleValueAction(
								'resolve_labels',
								'enabled',
								!rule.resolve_labels.enabled
							)
						)
					}
					helpText="The click source will be added to the blacklist, and visitor will be excluded on the perspective ad platform."
				/>
				{rule.resolve_labels.enabled && (
					<GInput
						name="resolve_labels"
						placeholder="Abusive, Unwanted"
						value={rule.resolve_labels.labels?.join(',')}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							dispatch(
								setObjectSimpleValueAction(
									'resolve_labels',
									'labels',
									e.target.value.split(',')
								)
							)
							clearError('resolve_labels')
						}}
						className="w-[40%]"
						error={errors?.resolve_labels}
						inputClassName='focus:ring-0 font-normal rounded-lg h-[38px]'
					/>
				)}
			</div>
			<div className="flex items-center h-[36px] gap-2">
				<GCheckbox
					name="blacklist"
					label="Blacklist the source"
					checked={!!rule.block}
					className="text-bold items-center"
					onChange={() => dispatch(toggleBlockAction())}
					helpText={`The click source will be added to the blacklist. System will automatically attempt to add the visitor to excluded audience, and exclude IP ${
						isRangeFocus ? 'range' : 'address'
					} on targeted campaign.`}
				/>
				{rule.block ? (
					<>
						<GInput
							name="block_period_value"
							placeholder="15"
							type="number"
							min={1}
							value={rule.block.period.value}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								dispatch(setPeriodValue('block', e.target.value))
								clearError('block_period_value')
							}}
							className="w-[75px]"
							errorPosition="absolute"
							errorClassName="mt-1 text-xs text-red-700"
							error={errors?.block_period_value}
							inputClassName='focus:ring-0 h-[38px] rounded-lg'
						/>
						<GComboBox
							options={PERIOD_OPTIONS}
							filter={(item, query) => item.toString() === query}
							value={PERIOD_OPTIONS.find(
								(o) => o.value === rule.block?.period.unit
							)}
							renderOption={(i) => i.label.toString()}
							renderValue={(i) => i.label.toString()}
							onChange={(val) => dispatch(setPeriodUnit('block', val.value))}
							className="w-[100px]"
							inputClass='text-base'
						/>
					</>
				) : null}
			</div>
			<div className="flex items-center h-[36px] gap-2">
				<GCheckbox
					name="temp_ip_exclusion"
					label={`Temporarily exclude IP ${
						isRangeFocus ? 'range' : 'address'
					} on targeted campaign`}
					checked={!!rule.block || !!rule.temp_ip_exclusion}
					disabled={!!rule.block}
					className="text-bold items-center"
					onChange={() => dispatch(toggleTempExclusionAction())}
					helpText={`(Google Ads only) Visitor’s IP ${
						isRangeFocus ? 'range' : 'address'
					} will be excluded on the targeted campaign for the selected time period.`}
				/>
				{rule.temp_ip_exclusion ? (
					<>
						<GInput
							name="temp_ip_exclusion_period_value"
							placeholder="15"
							type="number"
							min={1}
							value={rule.temp_ip_exclusion.period.value}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								dispatch(setPeriodValue('temp_ip_exclusion', e.target.value))
								clearError('temp_ip_exclusion_period_value')
							}}
							className="w-[50px]"
							errorPosition="absolute"
							errorClassName="mt-1 text-xs text-red-700"
							error={errors?.temp_ip_exclusion_period_value}
							inputClassName='focus:ring-0 rounded-lg h-[38px]'
						/>
						<GComboBox
							options={PERIOD_OPTIONS}
							filter={(item, query) => item.toString() === query}
							value={PERIOD_OPTIONS.find(
								(o) => o.value === rule.temp_ip_exclusion?.period.unit
							)}
							renderOption={(i) => i.label.toString()}
							renderValue={(i) => i.label.toString()}
							onChange={(val) =>
								dispatch(setPeriodUnit('temp_ip_exclusion', val.value))
							}
							className="w-[100px]"
							inputClass='text-base'
						/>
					</>
				) : null}
			</div>
			<div className="flex items-center h-[36px]">
				<GCheckbox
					name="audience_exclusion"
					label="Add visitor to excluded audience on targeted PPC account"
					checked={!!(rule.block || rule.audience_exclusion)}
					disabled={!!rule.block}
					className="text-medium text-t-google-ad items-center"
					onChange={() =>
						dispatch(
							setSimpleValueAction(
								'audience_exclusion',
								!rule.audience_exclusion
							)
						)
					}
					helpText="Visitor will be added to the excluded audience on for the ad platform. Audience is already excluded account-wide."
				/>
			</div>
		</section>
	)
}
